import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators.js';
import { html, type TemplateResult, nothing } from 'lit';
import { translate, get } from 'lit-translate';
import { container } from 'tsyringe';
import { DateTime, Interval } from 'luxon';
import type { PopoverListItem } from '@o2d/popover-list';
import type { LayoutTemplate } from '@o2d/grid-layout';
import type { ViewportItem } from '@o2d/datasource';
import { AbstractStandardTimelineView } from '../abstract-standard-timeline.view';
import type { TimelineViewConfig } from '../_shared/timeline-view.config';
import { TIMELINE_LAYOUT_TEMPLATE_T4 } from '../_shared/timeline-layout';
import type { ExtraFixedColumn } from '../_shared/types/extra-fixed-column.interface';
import type { HeaderItem } from '../_shared/types/header-item.type';
import { ProjectRepository } from '../../domain/repositories/project.repository';
import { IssueRepository } from '../../domain/repositories/issue.repository';
import { Issue } from '../../domain/entities/issue.entity';
import type { Entity } from '../../domain/entities/entity';
import { Project } from '../../domain/entities/project.entity';
import { AxisDataClient } from '../../connectivity/axis-data-client';
import { appState } from '../../app.state';
import { ToolbarHelper } from '../../helpers/toolbar.helper';
import { UiHelper } from '../../helpers/ui.helper';
import type { TimelineToolbarItem } from '../../types/timeline-toolbar-item.interface';
import { defaultTimelineToolbarItems } from '../../toolbar-items/o2d-default-toolbar-items';
import { createSimpleButtonToolbarItem } from '../../toolbar-items/simple-button-toolbar-item.factory';
import { ActionIds } from '../../actions/action.types';
import { effectiveBoardSort, type BoardSort } from '../../helpers/board-sort.helper';
import { BOARD_SORT_TOGGLE } from './board-sort-toggle';
import { tenantBoardSortDefault } from '../../tenant-feature-flags';
import type { SortConfig } from '../../connectivity/sort-config.types';

/**
 * Filters Y-axis to show only board-type projects (ProjectType.key = 'project').
 * The sort order is not fixed here: it follows the board-sort toggle, see `viewConfig`.
 */
const VIEW_CONFIG: Omit<TimelineViewConfig, 'sort'> = {
    entityType: 'Project',
    yItemPath: 'project.uid',
    includes: ['project', 'employees', 'keywords', 'creator', 'employeeToIssueAssignments'],
    queryParams: [{ key: 'type.key', comparator: 'eq', value: 'project' }]
};

/** Sort config per board-sort mode. `alphabetical` is the same order the Dailys board uses. */
const SORT_BY_MODE: Record<BoardSort, SortConfig> = {
    milestone: { strategy: 'milestoneDelta' },
    alphabetical: { strategy: 'property', path: 'title', direction: 'ASC' },
};

/** Y-cursor key per mode: each order remembers its own scroll position (see viewStateName). */
const VIEW_STATE_NAME_BY_MODE: Record<BoardSort, string> = {
    milestone: 'boards',
    alphabetical: 'boards-alphabetical',
};

/**
 * Timeline view grouping issues by project (board).
 * Route: /by-board
 *
 * Y-axis: Project — overrides populateTemplateFromYAxis to assign project.
 */
@customElement('o2d-boards-view')
export class BoardsView extends AbstractStandardTimelineView {
    /**
     * The sort order the axes are currently BUILT with — distinct from the order the user
     * wants (`_currentBoardSort()`, read live from appState). Everything that builds the axes or
     * writes Y-cursors reads this one (`viewConfig`, `viewStateName`, the start-cursor and
     * focus-today branches), so a rebuild in flight never mixes the old and the new order.
     * Only the toggle's label/icon and the toggle action read the wanted order.
     *
     * Set synchronously in connectedCallback BEFORE `super.connectedCallback()`, because the
     * base reads `viewConfig` in its synchronous Phase 2; a normal load therefore leaves built
     * and wanted equal and never triggers a second rebuild.
     */
    private _builtBoardSort: BoardSort = 'milestone';

    /** True while `applyBoardSort()` rebuilds; a second call during that time is a no-op. */
    private _isReinitializing = false;

    /** Unsubscriber of the appState `boardSort` subscription (see initBoardSortBridge). */
    private _boardSortUnsubscriber?: () => void;

    /**
     * Y-cursor key of the built order: `'boards'` (milestone) or `'boards-alphabetical'`. Each
     * order remembers its own position, so a milestone anchor never becomes the alphabetical
     * start row. The base only uses this name as the key of the remembered Y-cursor.
     */
    protected get viewStateName(): string {
        return VIEW_STATE_NAME_BY_MODE[this._builtBoardSort];
    }

    /** VIEW_CONFIG plus the sort order the axes are built with. */
    protected get viewConfig(): TimelineViewConfig {
        return { ...VIEW_CONFIG, sort: SORT_BY_MODE[this._builtBoardSort] };
    }

    /**
     * True when the tenant has no board projects (empty Y-axis origin).
     *
     * Set in createStartCursors() when the resolved D-cursor (Y-axis origin) is
     * empty. Rendering the o2d-timeline with an empty origin makes
     * entityAxisBuilder throw "Axis origin has no value - axis not initialized"
     * (viewport-query.types.ts:134), so render() shows an empty-board state
     * instead. Guards the initial-load path only — the runtime create/delete
     * path is covered by AbstractO2dTimelineView's data-provider-ready mitigation.
     */
    @state() private _isEmptyTenant = false;

    /**
     * Uses layout template t4 instead of the shared t3: same grid, plus one extra fixed
     * row-header column (`column-section-4`) between the add-issue button and the context
     * menu. The template name also prefixes the CSS custom properties the grid reads its
     * sizes from, so t4 pulls `--t4-*` from spa.css rather than `--t3-*`.
     *
     * @override
     */
    protected override createLayoutTemplate(): LayoutTemplate {
        return TIMELINE_LAYOUT_TEMPLATE_T4;
    }

    /**
     * Declares the extra column the t4 template makes room for: a button that creates a
     * milestone for this row's project.
     *
     * Only the cell CONTENT is declared here. Where the column sits, how wide it is, which
     * axis covers it and how far the row-header item spans are all derived from the layout
     * template above, so those cannot drift out of step with this declaration.
     *
     * @override
     */
    protected override getExtraFixedColumns(): ExtraFixedColumn[] {
        return [
            {
                sectionId: 'column-section-4',
                areaId: 'grid-area-milestone',
                // Required: the row-header look (border, background, centring) hangs off
                // `o2d-area.y-axis > div`; without it this cell is an unstyled gap.
                classes: ['y-axis'],
                cellRenderer: (item: ViewportItem<unknown>) => {
                    const data = item.data as HeaderItem;
                    const uid = data.entities?.[0]?.uid ?? '';
                    // The <i> carries no icon class on purpose: its two glyphs (square-plus
                    // base, flag-checkered badge) come from a selector rule on
                    // [data-role="add-milestone"] in timeline.css. That follows the
                    // go-forward convention in fonts/fontawesome.css — new icon usages
                    // define their own rule rather than extending the .icon-<name> set.
                    return html`<div class="btn" data-role="add-milestone" data-entity-uid="${uid}" title=${get('ha-boards-view.row-title.new-milestone')}><i></i></div>`;
                }
            }
        ];
    }

    /**
     * Registers the click delegation for the add-milestone button. The base class registers
     * its own three handlers in its `connectedCallback`, so `super` runs first here; unlike
     * the base's `_cursorX` assignment, listener registration depends on no ordering.
     *
     * @override
     */
    override async connectedCallback(): Promise<void> {
        // Both BEFORE super: the base reads viewConfig synchronously in its Phase 2, and the
        // subscription must exist before the first await so a fast navigation away (disconnect
        // during the await) cannot leave it attached to a detached view.
        this._builtBoardSort = this._currentBoardSort();
        this.initBoardSortBridge();

        await super.connectedCallback();
        this.addEventListener('click', this._onAddMilestoneButtonClick);
    }

    /** @override */
    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onAddMilestoneButtonClick);
        this._boardSortUnsubscriber?.();
        this._boardSortUnsubscriber = undefined;
    }

    /** The sort order the user wants: this browser's choice, else the tenant default. */
    private _currentBoardSort(): BoardSort {
        return effectiveBoardSort(appState.boardSort, tenantBoardSortDefault(globalThis.window?.__FEATURE_FLAGS__));
    }

    /**
     * Filtered appState subscription: rebuilds the board when the wanted sort order changes.
     * Same pattern as the base's initCellWidthMultiplicationBridge.
     */
    protected initBoardSortBridge(): void {
        this._boardSortUnsubscriber?.();
        this._boardSortUnsubscriber = appState.subscribe(() => {
            void this.applyBoardSort();
        }, 'boardSort');
    }

    /**
     * Rebuilds axes AND data providers until the built order matches the wanted one.
     *
     * - Loops instead of queueing: two fast clicks end in exactly one matching state, and a
     *   double click back to the built order rebuilds nothing. A second call while a rebuild
     *   runs returns at once; the running loop picks the new wish up when it checks again.
     *   Overlapping rebuilds would leave one waiting forever on the timeline's generation and
     *   end in its 5 s timeout with a console error.
     * - `_isLoading`: the toolbar is not rendered while loading (base render()), so the toggle
     *   cannot be pressed then. The guard only covers an abandoned load generation, where the
     *   base returns early and `_isLoading` stays true.
     * - Switching TO alphabetical clears its remembered cursor, so the list starts at the top
     *   (decision 5a). Only here — never on load, where the remembered position is kept (5b).
     * - Data providers are rebuilt too: the content provider (B×D) carries the sort order.
     */
    protected async applyBoardSort(): Promise<void> {
        if (this._isLoading || this._isReinitializing) return;

        this._isReinitializing = true;
        try {
            while (this._builtBoardSort !== this._currentBoardSort()) {
                const target = this._currentBoardSort();
                if (target === 'alphabetical') {
                    appState.setViewCursorY(VIEW_STATE_NAME_BY_MODE.alphabetical, '');
                }
                this._builtBoardSort = target;
                await this.reinitializeAxes({ rebuildDataProviders: true });
            }
        } finally {
            this._isReinitializing = false;
        }
    }

    /**
     * Default toolbar plus the board-sort toggle (order 15: between detail view and "add").
     *
     * Label and icon name what the next click does (Anke, 2026-10-07): "Alphabetisch sortieren"
     * with the A–Z icon while sorted by milestone, "Meilensteine sortieren" with the flag while
     * sorted alphabetically. No pressed/active state — a highlighted "Meilensteine sortieren"
     * would read as "sorted by milestone". Both follow the WANTED order, so they flip at once
     * on click; the board follows as soon as the rebuild is done.
     *
     * @override
     */
    protected override getToolbarItems(): TimelineToolbarItem[] {
        return [
            ...defaultTimelineToolbarItems(),
            createSimpleButtonToolbarItem({
                id: 'board-sort-toggle',
                cssClass: () => BOARD_SORT_TOGGLE[this._currentBoardSort()].cssClass,
                labelKey: () => BOARD_SORT_TOGGLE[this._currentBoardSort()].labelKey,
                actionId: ActionIds.BOARD_TOGGLE_SORT,
                position: 'start',
                order: 15,
            }),
        ];
    }

    /**
     * Click delegation handler for the row-header milestone button.
     *
     * Reuses `IssueRepository.createTimeFrame()`, which already produces exactly what is
     * needed — milestone type, status OPEN, title, creator, project — and is the same call
     * the project dialog's "Meilensteine" section makes. Then opens the create dialog on
     * that template, mirroring the add-issue button: the button pre-fills a dialog, it
     * never saves silently.
     *
     * Wrapped in try/catch rather than guarded with an `if`. `Repository.findOneById()`
     * does NOT return null for a missing entity — it REJECTS (repository.ts:59, and
     * findOneById itself does not catch), despite its `Promise<T | null>` signature. So a
     * null check would never be reached, and since this is an async DOM listener the
     * rejection would vanish as an unhandled promise: the button would appear to do
     * nothing at all. That case is reachable in practice — the milestone issue type is the
     * only seeded type with `isFixed = 0`, so a tenant is allowed to delete it, and
     * createTimeFrame() resolves that type unguarded too.
     *
     * Split in two on purpose. The listener itself must be an arrow-function class field,
     * because removeEventListener only matches the identical function reference — but an
     * instance field is unreachable from a test that drives prototype members against a
     * stand-in receiver. So the field is a one-line delegate and the logic sits on the
     * prototype, where handleAddMilestoneClick() can be called directly.
     */
    private _onAddMilestoneButtonClick = (evt: Event): Promise<void> => this.handleAddMilestoneClick(evt);

    /** Logic of the add-milestone button; see _onAddMilestoneButtonClick for the rationale. */
    protected async handleAddMilestoneClick(evt: Event): Promise<void> {
        const target = (evt.target as HTMLElement).closest('[data-role="add-milestone"]') as HTMLElement | null;
        if (!target) return;

        const uid = target.dataset.entityUid;
        if (!uid) return;

        try {
            const project = await container.resolve(ProjectRepository).findOneById(uid);
            if (!project) return;

            const today = DateTime.utc().startOf('day');
            const template = await container.resolve(IssueRepository)
                .createTimeFrame(project, Interval.fromDateTimes(today, today.plus({ days: 1 })));

            UiHelper.openDialogEntityEdit(Issue, template, { creationMode: true, closable: true });
        } catch (error) {
            console.error('[BoardsView] Could not prepare a milestone for this row:', error);
            UiHelper.notify({
                level: 'error',
                title: get('ha-boards-view.row-title.new-milestone'),
                content: html`${translate('ha-boards-view.row-title.new-milestone-failed')}`,
                closable: true
            });
        }
    }

    /**
     * Fetches the anchor entity for the focus-today position.
     * Returns the anchorUid (2 positions above closest-to-today), or '' if no projects exist.
     *
     * @returns Promise resolving to the anchor ShortId, or empty string if no projects exist
     */
    private async _fetchFocusTodayAnchor(): Promise<string> {
        const axisDataClient = container.resolve(AxisDataClient);
        const config = this.viewConfig;
        const response = await axisDataClient.fetchFocusTodayAnchor({
            entityType: config.entityType,
            sort: config.sort,
            queryParams: config.queryParams,
        });
        return response.anchorUid ?? '';
    }

    /**
     * Milestone order: overrides the base createStartCursors() to always fetch the
     * closest-to-today anchor from the backend instead of reading from localStorage (prevents
     * stale Y-cursor). Alphabetical order: the base result as is — the remembered position of
     * 'boards-alphabetical', else the first project (the anchor endpoint only accepts the
     * milestone order and answers 400 to any other).
     *
     * Falls back to super.createStartCursors() if the API call fails, so the view
     * still loads correctly even when the new endpoint is unavailable.
     *
     * @override
     */
    protected override async createStartCursors(): Promise<{ axisId: string; value: string | number }[]> {
        const cursors = await this._resolveStartCursors();

        // Empty-tenant guard: an empty D-cursor (Y-axis origin) means no board
        // projects exist. Feeding it to the o2d-timeline makes entityAxisBuilder
        // throw "Axis origin has no value" (viewport-query.types.ts:134), so flag
        // the empty state and let render() short-circuit to an empty board.
        const yCursor = cursors.find(cursor => cursor.axisId === 'D');
        this._isEmptyTenant = !yCursor?.value;

        return cursors;
    }

    /**
     * Resolves the start cursors, replacing the Y-axis (D) cursor with the
     * freshly fetched focus-today anchor when board projects exist.
     *
     * Returns the base cursors unchanged when no anchor is available (no
     * projects) or when the anchor fetch fails, so the view still loads.
     */
    private async _resolveStartCursors(): Promise<{ axisId: string; value: string | number }[]> {
        const baseCursors = await super.createStartCursors();

        if (this._builtBoardSort === 'alphabetical') {
            return baseCursors;
        }

        try {
            const anchorUid = await this._fetchFocusTodayAnchor();

            if (!anchorUid) {
                // No projects exist — return base cursors as-is
                return baseCursors;
            }

            // Persist anchor for navigation-back UX (anchorUid, not closestUid)
            appState.setViewCursorY(this.viewStateName, anchorUid);

            // Replace only the D-cursor with the fresh anchor position
            return baseCursors.map(cursor =>
                cursor.axisId === 'D' ? { axisId: 'D', value: anchorUid } : cursor
            );
        } catch (error) {
            console.error('[BoardsView] Error fetching focus-today anchor for start cursors, falling back to base:', error);
            // Fall back to super result on error (uses fetchFirstEntityUid() from base class)
            return baseCursors;
        }
    }

    /**
     * Renders an empty-board state instead of the o2d-timeline when the tenant
     * has no board projects. Prevents the o2d-timeline from constructing a
     * Y-axis with an empty origin (entityAxisBuilder throw). Falls back to the
     * base render (loading state + timeline) in all other cases.
     *
     * @override
     */
    protected override render(): TemplateResult | typeof nothing {
        if (!this._isLoading && this._isEmptyTenant) {
            return this._renderEmptyBoard();
        }
        return super.render();
    }

    /** Renders the toolbar plus an empty-state message (no o2d-timeline). */
    private _renderEmptyBoard(): TemplateResult {
        const toolbar = ToolbarHelper.renderTimelineToolbar(this.getToolbarItems());
        return html`
            <div class="view__body">
                <div class="view__column">
                    ${toolbar}
                    <p class="ha-boards-view__empty">${translate('ha-boards-view.empty')}</p>
                </div>
            </div>
        `;
    }

    /**
     * Scrolls Y-axis to the project with MIN(ABS(milestoneDelta)) when "Focus on Today" fires
     * (milestone order only; alphabetical scrolls the time axis only).
     *
     * Fetches the anchor entity (2 positions above closest-to-today) via the new endpoint
     * so the nearest-deadline project appears at approximately row 3 of the viewport.
     *
     * Guard: Ignores the event if the view is still loading (_isLoading = true) to prevent
     * a race condition where a click interrupts the initial load sequence.
     *
     * @override
     */
    protected override async onFocusToday(): Promise<void> {
        if (this._isLoading) return;

        // Time (X) axis → today (base behavior: rebuilds the axes around today).
        await super.onFocusToday();

        // Alphabetical order: horizontal only (Anke's decision 2) — the anchor endpoint
        // only accepts the milestone order.
        if (this._builtBoardSort === 'alphabetical') return;

        // Y axis → project nearest to today: live-scroll to the backend anchor
        // (closest-to-today minus 2 rows, so the nearest-milestone project sits at ~row 3).
        // The Y (entity) axis supports live scrollToValue (unlike the datetime X axis).
        try {
            const anchorUid = await this._fetchFocusTodayAnchor();
            if (anchorUid) {
                await this._timeline?.scrollToValue('D', anchorUid);
            }
        } catch (error) {
            console.error('[BoardsView] Error scrolling Y-axis to today:', error);
        }
    }

    /** Assigns clicked project to template issue. */
    protected override async populateTemplateFromYAxis(template: Issue, yAxisUid: string): Promise<void> {
        const projectRepo = container.resolve(ProjectRepository);
        const project = await projectRepo.findOneById(yAxisUid);
        if (project) {
            template.project = project;
        }
    }

    /**
     * Adds an "open link" entry to the row context menu, sourced from Project.url.
     * Every row BoardsView produces (main D-axis rows and the shared calendar row,
     * both filtered to type.key='project' — see VIEW_CONFIG) resolves to a Project
     * entity, so a plain cast is sufficient (mirrors IssueStatusView's pattern).
     *
     * @override
     */
    protected override buildRowContextMenu(entity: Entity): PopoverListItem[] {
        const project = entity as Project;

        return [
            {
                id: 'open-link',
                label: get('ha-boards-view.row-title.open-link'),
                type: 'item',
                disabled: !project.url,
                payload: project.url
            },
            { id: 'edit', label: get('ha-boards-view.row-title.edit'), type: 'item' },
            { id: 'delete', label: get('ha-boards-view.row-title.delete'), type: 'item' },
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'o2d-boards-view': BoardsView;
    }
}
